/**
 * WeaveIn.ai Platform - Communication Templates Management
 * Manage templates for 1-1 and bulk messaging
 * Includes bilingual (English/Spanish) support for CBO requirements
 */

import React, { useState, useEffect, useRef } from 'react';
import { config } from '@/src/config';
import { Languages, Globe } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { insertVariableAtCursor } from '@/hooks/useTemplateVariables';
import { VariablePicker, TemplatePreviewButton, TemplateCheckNotice, useTemplateCheck } from '@/components/messaging/templateVariables';
import { useComplianceMode } from '@/hooks/useComplianceMode';
import { RegulatedOnly } from '@/components/compliance/RegulatedOnly';
import { useTabbedChrome } from '@/components/weavein';

type TemplateStatus = 'draft' | 'in_review' | 'approved' | 'published' | 'rejected' | 'archived';

const STATUS_BADGE_CLASSES: Record<TemplateStatus, string> = {
  draft: 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300',
  in_review: 'bg-amber-100 text-amber-900',
  approved: 'bg-blue-100 text-blue-900',
  published: 'bg-green-100 text-green-900',
  rejected: 'bg-red-100 text-red-900',
  archived: 'bg-gray-200 text-gray-700',
};

const STATUS_LABELS: Record<TemplateStatus, string> = {
  draft: 'Draft',
  in_review: 'In Review',
  approved: 'Approved',
  published: 'Published',
  rejected: 'Rejected',
  archived: 'Archived',
};

const resolveTemplateStatus = (template: { status?: TemplateStatus | null; is_active?: boolean }): TemplateStatus => {
  if (template.status) return template.status;
  return template.is_active ? 'published' : 'draft';
};

const buildTemplateAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
  };
};

// Tone types for template messaging
type ToneType = 'formal' | 'friendly' | 'urgent';

// Language mode for templates
type LanguageMode = 'english' | 'spanish' | 'bilingual';

interface VariableField {
  field_name: string;
  field_label: string;
  field_type: string;
}

interface Template {
  id: number;
  cbo_id: number;
  name: string;
  description: string | null;
  template_type: string;
  communication_method: string;
  subject: string | null;
  body: string;
  variable_fields: string[];
  is_active: boolean;
  created_ts: string;
  updated_ts: string;
  created_by: number;
  created_by_name: string | null;
  status?: TemplateStatus | null;
  version?: number | null;
}

interface TemplateLifecycle {
  template?: { id: number; status?: TemplateStatus | null; version?: number | null; updated_ts?: string };
  versions?: Array<Record<string, any>>;
  latest_approval?: {
    id: number;
    status: string;
    submitted_at?: string | null;
    closed_at?: string | null;
  } | null;
}

interface CommunicationTemplatesProps {
  onBack?: () => void;
  /**
   * Bump from a parent to force a refetch (e.g. after a Universal-Library
   * template has been imported in a sibling tab). Treated as a dependency
   * of the list-fetch effect — value content is irrelevant, only changes.
   */
  refreshKey?: number;
}

const CommunicationTemplates: React.FC<CommunicationTemplatesProps> = ({ onBack, refreshKey }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const tabbed = useTabbedChrome();
  const [templates, setTemplates] = useState<Template[]>([]);
  // Validate merge fields on save (errors block; warnings show inline).
  const templateCheck = useTemplateCheck('messages');
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [filterMethod, setFilterMethod] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const { isRegulated } = useComplianceMode();
  const [lifecycle, setLifecycle] = useState<TemplateLifecycle | null>(null);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [lifecycleAction, setLifecycleAction] = useState<null | 'submit' | 'publish' | 'withdraw' | 'archive'>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template_type: '1-1',
    communication_method: 'email',
    subject: '',
    body: '',
    variable_fields: [] as string[],
    is_active: true,
    // Bilingual fields
    language_mode: 'english' as LanguageMode,
    body_es: '',
    subject_es: '',
    tone: 'friendly' as ToneType,
  });

  useEffect(() => {
    fetchTemplates();
  }, [filterMethod, filterType, refreshKey]);

  const fetchLifecycle = async (templateId: number) => {
    setLifecycleLoading(true);
    try {
      const res = await fetch(
        `${config.weaveinApi.baseUrl}/api/communication-templates/${templateId}/lifecycle`,
        { headers: buildTemplateAuthHeaders() },
      );
      if (res.ok) {
        const data = await res.json();
        setLifecycle(data);
      } else {
        setLifecycle(null);
      }
    } catch {
      setLifecycle(null);
    } finally {
      setLifecycleLoading(false);
    }
  };

  const callLifecycleAction = async (
    templateId: number,
    action: 'submit-for-review' | 'publish' | 'withdraw' | 'archive',
    actionKey: 'submit' | 'publish' | 'withdraw' | 'archive',
  ) => {
    setLifecycleAction(actionKey);
    try {
      const res = await fetch(
        `${config.weaveinApi.baseUrl}/api/communication-templates/${templateId}/${action}`,
        { method: 'POST', headers: buildTemplateAuthHeaders() },
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to ${action.replace(/-/g, ' ')}`);
      }
      await fetchTemplates();
      if (editingTemplate) {
        await fetchLifecycle(editingTemplate.id);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setLifecycleAction(null);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams();
      if (filterMethod !== 'all') params.append('communication_method', filterMethod);
      if (filterType !== 'all') params.append('template_type', filterType);

      const response = await fetch(
        `${config.weaveinApi.baseUrl}/api/communication-templates?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        throw new Error('Failed to fetch templates');
      }
    } catch (err: any) {
      if (!error) {
        setError(err.message || 'Failed to load templates. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };


  /** The texts to validate: English, plus Spanish when the template has it. */
  const templateParts = () => {
    const subject = formData.communication_method === 'email' ? formData.subject : null;
    const parts = [{ subject, body: formData.body }];
    if (formData.language_mode === 'bilingual') {
      parts.push({ subject: formData.communication_method === 'email' ? formData.subject_es : null, body: formData.body_es });
    }
    return parts;
  };

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(await templateCheck.run(templateParts()))) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.weaveinApi.baseUrl}/api/communication-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Template created successfully!');
        setShowCreateForm(false);
        resetForm();
        fetchTemplates();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create template');
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleUpdateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTemplate) return;
    if (!(await templateCheck.run(templateParts()))) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${config.weaveinApi.baseUrl}/api/communication-templates/${editingTemplate.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        alert('Template updated successfully!');
        setEditingTemplate(null);
        resetForm();
        fetchTemplates();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update template');
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.weaveinApi.baseUrl}/api/communication-templates/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
        },
      });

      if (response.ok) {
        alert('Template deleted successfully!');
        fetchTemplates();
      } else {
        throw new Error('Failed to delete template');
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const startEdit = (template: Template) => {
    setEditingTemplate(template);
    setLifecycle(null);
    fetchLifecycle(template.id);
    // Parse bilingual content if stored as JSON, otherwise use as-is
    let bodyContent = template.body;
    let bodyEs = '';
    let subjectEs = '';
    let languageMode: LanguageMode = 'english';
    let tone: ToneType = 'friendly';

    // Check if body contains JSON bilingual content
    try {
      const parsed = JSON.parse(template.body);
      if (parsed.en && typeof parsed.en === 'string') {
        bodyContent = parsed.en;
        bodyEs = parsed.es || '';
        languageMode = parsed.es ? 'bilingual' : 'english';
        tone = parsed.tone || 'friendly';
      }
    } catch {
      // Body is plain text, not JSON
    }

    // Check subject for bilingual
    try {
      if (template.subject) {
        const parsedSubject = JSON.parse(template.subject);
        if (parsedSubject.en) {
          subjectEs = parsedSubject.es || '';
        }
      }
    } catch {
      // Subject is plain text
    }

    setFormData({
      name: template.name,
      description: template.description || '',
      template_type: template.template_type,
      communication_method: template.communication_method,
      subject: template.subject || '',
      body: bodyContent,
      variable_fields: template.variable_fields,
      is_active: template.is_active,
      language_mode: languageMode,
      body_es: bodyEs,
      subject_es: subjectEs,
      tone: tone,
    });
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      template_type: '1-1',
      communication_method: 'email',
      subject: '',
      body: '',
      variable_fields: [],
      is_active: true,
      language_mode: 'english',
      body_es: '',
      subject_es: '',
      tone: 'friendly',
    });
    setEditingTemplate(null);
  };

  const insertVariable = (fieldName: string) => {
    const newBody = insertVariableAtCursor(bodyTextareaRef, formData.body, fieldName);
    setFormData((prev) => ({
      ...prev,
      body: newBody,
      variable_fields: prev.variable_fields.includes(fieldName)
        ? prev.variable_fields
        : [...prev.variable_fields, fieldName],
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center space-x-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors active:scale-95 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label={t('admin.go_back', 'Go back')}
              >
                <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div>
              {!tabbed && <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100">{t('admin.communication_templates', 'Communication Templates')}</h1>}
              <p className={`text-sm md:text-base text-gray-600 dark:text-gray-400 ${tabbed ? '' : 'mt-1'}`}>
                {t('admin.manage_templates_for_11_and', 'Manage templates for 1-1 and bulk messaging')}
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 active:scale-95 transition-all min-h-[44px]"
          >
            {t('admin.create_template', 'Create Template')}
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('admin.communication_method', 'Communication Method')}
              </label>
              <select
                value={filterMethod}
                onChange={(e) => setFilterMethod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('admin.all_methods', 'All Methods')}</option>
                <option value="email">{t('admin.email', 'Email')}</option>
                <option value="sms">SMS</option>
                <option value="phone">{t('admin.phone', 'Phone')}</option>
                <option value="voice">{t('admin.voice_ai', 'Voice AI')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('admin.template_type', 'Template Type')}</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('admin.all_types', 'All Types')}</option>
                <option value="1-1">1-1 Messaging</option>
                <option value="bulk_email">{t('admin.bulk_email', 'Bulk Email')}</option>
                <option value="bulk_sms">{t('admin.bulk_sms', 'Bulk SMS')}</option>
                <option value="email">{t('admin.email_1', 'Email')}</option>
                <option value="sms">SMS</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="in_review">In Review</option>
                <option value="approved">Approved</option>
                <option value="published">Published</option>
                <option value="rejected">Rejected</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>

        {/* Templates List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('admin.loading_templates', 'Loading templates...')}</p>
          </div>
        ) : error ? (
          <div className="border rounded-xl p-8 text-center bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
            <svg
              className="mx-auto h-16 w-16 mb-4 text-red-400 dark:text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-semibold mb-2 text-red-900 dark:text-red-100">
              {t('admin.error_loading_templates', 'Error Loading Templates')}
            </h3>
            <p className="mb-6 text-red-600 dark:text-red-400">
              {error}
            </p>
            <button
              onClick={fetchTemplates}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('admin.try_again', 'Try Again')}
            </button>
          </div>
        ) : (() => {
          const visibleTemplates =
            filterStatus === 'all'
              ? templates
              : templates.filter((tpl) => resolveTemplateStatus(tpl) === filterStatus);
          return visibleTemplates.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('admin.no_templates_found', 'No templates found')}</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              {t('admin.create_your_first_template', 'Create Your First Template')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {visibleTemplates.map((template) => {
              const status = resolveTemplateStatus(template);
              return (
              <div
                key={template.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{template.name}</h3>
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${STATUS_BADGE_CLASSES[status]}`}
                      >
                        {STATUS_LABELS[status]}
                      </span>
                      {!template.is_active && (
                        <span className="px-2 py-1 text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                          {t('admin.inactive', 'Inactive')}
                        </span>
                      )}
                      <span className="px-2 py-1 text-xs font-semibold bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-300 rounded-full">
                        {template.communication_method.toUpperCase()}
                      </span>
                      <span className="px-2 py-1 text-xs font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 rounded-full">
                        {template.template_type}
                      </span>
                      {/* Bilingual indicator - check if body contains JSON with es field */}
                      {(() => {
                        try {
                          const parsed = JSON.parse(template.body);
                          if (parsed.es) {
                            return (
                              <span className="px-2 py-1 text-xs font-semibold bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 rounded-full flex items-center gap-1">
                                <Languages className="w-3 h-3" />
                                {t('admin.bilingual', 'Bilingual')}
                              </span>
                            );
                          }
                        } catch {
                          // Not bilingual
                        }
                        return null;
                      })()}
                    </div>
                    {template.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{template.description}</p>
                    )}
                    {template.subject && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                        <span className="font-medium">{t('admin.subject', 'Subject:')}</span> {template.subject}
                      </p>
                    )}
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">{template.body}</p>
                    {template.variable_fields.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{t('admin.variables', 'Variables:')}</span>
                        {template.variable_fields.map((field) => (
                          <span
                            key={field}
                            className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                          >
                            {`{{${field}}}`}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Created by {template.created_by_name || 'Unknown'} on{' '}
                      {fmt.short(template.created_ts)}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 justify-end">
                    {/* Lifecycle actions — only when org is regulated */}
                    {isRegulated && (status === 'draft' || status === 'rejected') && (
                      <button
                        onClick={() => callLifecycleAction(template.id, 'submit-for-review', 'submit')}
                        disabled={lifecycleAction === 'submit'}
                        className="px-3 py-2 text-sm bg-amber-50 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/60 transition-colors disabled:opacity-50"
                        title="Submit for compliance review"
                      >
                        {lifecycleAction === 'submit' ? 'Submitting…' : 'Submit for review'}
                      </button>
                    )}
                    {isRegulated && status === 'in_review' && (
                      <a
                        href={`/admin/approvals?subject_kind=communication_template&subject_id=${template.id}`}
                        className="px-3 py-2 text-sm bg-amber-50 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/60 transition-colors inline-flex items-center"
                        title="View approval request"
                      >
                        View approval
                      </a>
                    )}
                    {isRegulated && status === 'approved' && (
                      <button
                        onClick={() => callLifecycleAction(template.id, 'publish', 'publish')}
                        disabled={lifecycleAction === 'publish'}
                        className="px-3 py-2 text-sm bg-green-50 dark:bg-green-900/40 text-green-800 dark:text-green-200 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/60 transition-colors disabled:opacity-50"
                        title="Publish the approved template"
                      >
                        {lifecycleAction === 'publish' ? 'Publishing…' : 'Publish'}
                      </button>
                    )}
                    <button
                      onClick={() => startEdit(template)}
                      className="px-3 py-2 text-sm bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
                    >
                      {t('admin.edit', 'Edit')}
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="px-3 py-2 text-sm bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 transition-colors"
                    >
                      {t('admin.delete', 'Delete')}
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          );
        })()}

        {/* Create/Edit Form Modal */}
        {showCreateForm && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50"
            onClick={() => {
              setShowCreateForm(false);
              resetForm();
            }}
          >
            <div
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl ${editingTemplate && isRegulated ? 'max-w-6xl' : 'max-w-4xl'} w-full max-h-[90vh] overflow-y-auto`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                  {editingTemplate ? 'Edit Template' : 'Create New Template'}
                </h2>
                {editingTemplate && (() => {
                  const editingStatus = resolveTemplateStatus(editingTemplate);
                  return (
                    <RegulatedOnly alwaysRender>
                      <div className="mb-4 flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${STATUS_BADGE_CLASSES[editingStatus]}`}>
                          {STATUS_LABELS[editingStatus]}
                        </span>
                        {isRegulated && editingStatus === 'in_review' && (
                          <span className="text-xs text-amber-700">Editor is read-only while in review.</span>
                        )}
                      </div>
                    </RegulatedOnly>
                  );
                })()}

                {/* Review-required banner — explains the lifecycle for regulated orgs */}
                {isRegulated && (() => {
                  const editingStatus = editingTemplate
                    ? resolveTemplateStatus(editingTemplate)
                    : 'draft';
                  if (editingStatus === 'published') return null;
                  return (
                    <div className="mb-5 flex items-start gap-3 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
                      <svg className="w-4 h-4 text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-7-8a7 7 0 1114 0 7 7 0 01-14 0zm7.75-3.25a.75.75 0 00-1.5 0v3.5a.75.75 0 00.22.53l2 2a.75.75 0 101.06-1.06l-1.78-1.78V6.75z" clipRule="evenodd" />
                      </svg>
                      <div className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                        <strong>Review required.</strong> This org runs in regulated mode. Templates
                        move <code>draft → in_review → approved → published</code>. Save your edits
                        as a draft, then click <strong>Submit for review</strong> on the template
                        list. An approver from the configured workflow signs off, then you can
                        publish. Configure who reviews in <strong>Org Settings → Compliance →
                        Approval workflows</strong>.
                      </div>
                    </div>
                  );
                })()}

                <div className={editingTemplate && isRegulated ? 'grid grid-cols-1 lg:grid-cols-3 gap-6' : ''}>
                  <div className={`${editingTemplate && isRegulated ? 'lg:col-span-2' : ''} ${editingTemplate && isRegulated && resolveTemplateStatus(editingTemplate) === 'in_review' ? 'opacity-60 pointer-events-none' : ''}`}>
                <form onSubmit={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.template_name', 'Template Name')}<span className="text-red-600 dark:text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>

                    {/* Template Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.template_type_1', 'Template Type')}<span className="text-red-600 dark:text-red-400">*</span>
                      </label>
                      <select
                        value={formData.template_type}
                        onChange={(e) => setFormData({ ...formData, template_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="1-1">1-1 Messaging</option>
                        <option value="bulk_email">{t('admin.bulk_email_1', 'Bulk Email')}</option>
                        <option value="bulk_sms">{t('admin.bulk_sms_1', 'Bulk SMS')}</option>
                        <option value="bulk_voice">{t('admin.bulk_voice_ai', 'Bulk Voice AI')}</option>
                        <option value="email">{t('admin.email_2', 'Email')}</option>
                        <option value="sms">SMS</option>
                        <option value="voice">{t('admin.voice_ai_1', 'Voice AI')}</option>
                        <option value="voice_workflow">{t('admin.voice_workflow', 'Voice Workflow')}</option>
                      </select>
                    </div>

                    {/* Communication Method */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.communication_method_1', 'Communication Method')}<span className="text-red-600 dark:text-red-400">*</span>
                      </label>
                      <select
                        value={formData.communication_method}
                        onChange={(e) =>
                          setFormData({ ...formData, communication_method: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="email">{t('admin.email_3', 'Email')}</option>
                        <option value="sms">SMS</option>
                        <option value="phone">{t('admin.phone_1', 'Phone')}</option>
                        <option value="voice">{t('admin.voice_ai_2', 'Voice AI')}</option>
                      </select>
                    </div>

                    {/* Active Status */}
                    <div className="flex items-center">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="w-5 h-5 text-teal-600 border-gray-300 dark:border-gray-600 rounded focus:ring-teal-500 bg-white dark:bg-gray-700"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('admin.active', 'Active')}</span>
                      </label>
                    </div>
                  </div>

                  {/* Bilingual Settings Section */}
                  <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2 mb-4">
                      <Languages className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                        {t('admin.bilingual_settings', 'Bilingual Settings')}
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Language Mode */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <Globe className="w-4 h-4 inline mr-1" />
                          {t('admin.language_mode', 'Language Mode')}
                        </label>
                        <select
                          value={formData.language_mode}
                          onChange={(e) => setFormData({ ...formData, language_mode: e.target.value as LanguageMode })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="english">{t('admin.english_only', 'English Only')}</option>
                          <option value="spanish">{t('admin.spanish_only', 'Spanish Only')}</option>
                          <option value="bilingual">{t('admin.bilingual_english_spanish', 'Bilingual (English & Spanish)')}</option>
                        </select>
                      </div>

                      {/* Tone */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('admin.message_tone', 'Message Tone')}
                        </label>
                        <select
                          value={formData.tone}
                          onChange={(e) => setFormData({ ...formData, tone: e.target.value as ToneType })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="friendly">{t('admin.friendly', 'Friendly')}</option>
                          <option value="formal">{t('admin.formal', 'Formal')}</option>
                          <option value="urgent">{t('admin.urgent', 'Urgent')}</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('admin.description', 'Description')}</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  {/* Subject (for email) */}
                  {formData.communication_method === 'email' && (
                    <>
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {formData.language_mode === 'bilingual'
                            ? t('admin.email_subject_english', 'Email Subject (English)')
                            : formData.language_mode === 'spanish'
                              ? t('admin.email_subject_spanish', 'Email Subject (Spanish)')
                              : t('admin.email_subject', 'Email Subject')}
                          <span className="text-red-600 dark:text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          required={formData.communication_method === 'email'}
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                          placeholder={formData.language_mode === 'spanish'
                            ? "Use {{nombre_variable}} para contenido dinamico"
                            : "Use {{variable_name}} for dynamic content"}
                        />
                      </div>
                      {/* Spanish Subject (only in bilingual mode) */}
                      {formData.language_mode === 'bilingual' && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('admin.email_subject_spanish', 'Email Subject (Spanish)')}
                            <span className="text-red-600 dark:text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            required={formData.language_mode === 'bilingual'}
                            value={formData.subject_es}
                            onChange={(e) => setFormData({ ...formData, subject_es: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                            placeholder="Use {{nombre_variable}} para contenido dinamico"
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Body - English or Primary */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {formData.language_mode === 'bilingual'
                        ? t('admin.message_body_english', 'Message Body (English)')
                        : formData.language_mode === 'spanish'
                          ? t('admin.message_body_spanish', 'Message Body (Spanish)')
                          : t('admin.message_body', 'Message Body')}
                      <span className="text-red-600 dark:text-red-400">*</span>
                    </label>
                    <textarea
                      ref={bodyTextareaRef}
                      required
                      value={formData.body}
                      onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder={formData.language_mode === 'spanish'
                        ? "Escriba su mensaje aqui. Use {{nombre_variable}} para contenido dinamico."
                        : "Type your message here. Use {{variable_name}} for dynamic content."}
                    />
                  </div>

                  {/* Body - Spanish (only shown in bilingual mode) */}
                  {formData.language_mode === 'bilingual' && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.message_body_spanish', 'Message Body (Spanish)')}
                        <span className="text-red-600 dark:text-red-400">*</span>
                      </label>
                      <textarea
                        required={formData.language_mode === 'bilingual'}
                        value={formData.body_es}
                        onChange={(e) => setFormData({ ...formData, body_es: e.target.value })}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                        placeholder="Escriba su mensaje en espanol aqui. Use {{nombre_variable}} para contenido dinamico."
                      />
                    </div>
                  )}

                  {/* Variables: pick from the org's registry, preview, and validation results */}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <VariablePicker appliesTo="messages" onInsert={insertVariable} />
                    <TemplatePreviewButton
                      appliesTo="messages"
                      subject={formData.communication_method === 'email' ? formData.subject : null}
                      body={formData.body}
                    />
                  </div>
                  <div className="mt-3">
                    <TemplateCheckNotice result={templateCheck.result} />
                  </div>

                  {/* Form Actions */}
                  <div className="flex flex-wrap gap-3 mt-8">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateForm(false);
                        resetForm();
                      }}
                      className="flex-1 min-w-[120px] px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      {t('admin.cancel', 'Cancel')}
                    </button>
                    {(() => {
                      const editingStatus = editingTemplate ? resolveTemplateStatus(editingTemplate) : null;
                      if (editingTemplate && isRegulated && editingStatus === 'in_review') {
                        return null;
                      }
                      if (
                        editingTemplate &&
                        isRegulated &&
                        (editingStatus === 'draft' || editingStatus === 'rejected')
                      ) {
                        return (
                          <button
                            type="button"
                            disabled={lifecycleAction === 'submit'}
                            onClick={() =>
                              callLifecycleAction(editingTemplate.id, 'submit-for-review', 'submit')
                            }
                            className="flex-1 min-w-[160px] px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                          >
                            {lifecycleAction === 'submit' ? 'Submitting…' : 'Submit for Review'}
                          </button>
                        );
                      }
                      if (editingTemplate && isRegulated && editingStatus === 'approved') {
                        return (
                          <button
                            type="button"
                            disabled={lifecycleAction === 'publish'}
                            onClick={() =>
                              callLifecycleAction(editingTemplate.id, 'publish', 'publish')
                            }
                            className="flex-1 min-w-[160px] px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            {lifecycleAction === 'publish' ? 'Publishing…' : 'Publish'}
                          </button>
                        );
                      }
                      return (
                        <button
                          type="submit"
                          className="flex-1 min-w-[160px] px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                        >
                          {editingTemplate ? 'Update Template' : 'Create Template'}
                        </button>
                      );
                    })()}
                  </div>
                </form>
                  </div>
                  {editingTemplate && isRegulated && (
                    <RegulatedOnly>
                      <aside className="lg:col-span-1 bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4 border border-gray-200 dark:border-gray-700 self-start">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                          Approval Lifecycle
                        </h3>
                        {lifecycleLoading ? (
                          <p className="text-xs text-gray-500">Loading…</p>
                        ) : (
                          <div className="space-y-2 text-sm">
                            {lifecycle?.template?.version != null && (
                              <p className="text-gray-700 dark:text-gray-300">
                                <span className="font-medium">Version:</span> {lifecycle.template.version}
                              </p>
                            )}
                            {lifecycle?.latest_approval ? (
                              <>
                                <p className="text-gray-700 dark:text-gray-300">
                                  <span className="font-medium">Latest request:</span>{' '}
                                  {lifecycle.latest_approval.status}
                                </p>
                                {lifecycle.latest_approval.submitted_at && (
                                  <p className="text-xs text-gray-500">
                                    Submitted {fmt.short(lifecycle.latest_approval.submitted_at)}
                                  </p>
                                )}
                                {lifecycle.latest_approval.closed_at && (
                                  <p className="text-xs text-gray-500">
                                    Closed {fmt.short(lifecycle.latest_approval.closed_at)}
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="text-xs text-gray-500">No approval requests yet.</p>
                            )}
                            {resolveTemplateStatus(editingTemplate) === 'in_review' && (
                              <button
                                type="button"
                                disabled={lifecycleAction === 'withdraw'}
                                onClick={() =>
                                  callLifecycleAction(editingTemplate.id, 'withdraw', 'withdraw')
                                }
                                className="mt-3 w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                              >
                                {lifecycleAction === 'withdraw' ? 'Withdrawing…' : 'Withdraw'}
                              </button>
                            )}
                            {resolveTemplateStatus(editingTemplate) === 'published' && (
                              <button
                                type="button"
                                disabled={lifecycleAction === 'archive'}
                                onClick={() =>
                                  callLifecycleAction(editingTemplate.id, 'archive', 'archive')
                                }
                                className="mt-3 w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                              >
                                {lifecycleAction === 'archive' ? 'Archiving…' : 'Archive'}
                              </button>
                            )}
                          </div>
                        )}
                      </aside>
                    </RegulatedOnly>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunicationTemplates;
